import OpenAI from 'openai';
import { supabase } from '../lib/supabase';

interface OpenAIConfig {
  apiKey: string;
  organizationId?: string;
}

interface VectorStore {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

class OpenAIService {
  private clients: Map<string, OpenAI> = new Map();
  private configs: Map<string, OpenAIConfig> = new Map();
  private readonly TIMEOUT = 60000; // 60 seconds timeout
  private readonly API_BASE_URL = 'https://api.openai.com/v1';

  async getClient(configId: string): Promise<OpenAI> {
    try {
      // Return cached client if exists
      if (this.clients.has(configId)) {
        return this.clients.get(configId)!;
      }

      // Get API configuration
      const { data: config, error } = await supabase
        .from('api_configuraciones')
        .select('*')
        .eq('id', configId)
        .single();

      if (error) throw new Error(`Error al obtener la configuración: ${error.message}`);
      if (!config) throw new Error('Configuración no encontrada');
      if (!config.api_key) throw new Error('API key no configurada');
      if (config.estado !== 'activo') throw new Error('La configuración de API no está activa');

      // Create new client with proper configuration
      const client = new OpenAI({
        apiKey: config.api_key.startsWith('sk-') ? config.api_key : `sk-${config.api_key}`,
        organization: config.organization_id || undefined,
        dangerouslyAllowBrowser: true, // Add this line to fix the browser error
        timeout: this.TIMEOUT,
        maxRetries: 3
      });

      // Test connection and validate API key
      try {
        const models = await client.models.list();
        
        if (!models.data || models.data.length === 0) {
          throw new Error('No se pudo obtener la lista de modelos');
        }
      } catch (error: any) {
        if (error.status === 401) {
          throw new Error('API key inválida o expirada');
        }
        throw error;
      }

      // Cache client
      this.clients.set(configId, client);
      return client;
    } catch (error: any) {
      // Handle specific OpenAI API errors
      if (error.status === 401) {
        throw new Error('API key inválida o expirada');
      } else if (error.status === 403) {
        throw new Error('No tienes permiso para acceder a la API de OpenAI');
      } else if (error.status === 429) {
        throw new Error('Se ha excedido el límite de la API');
      } else if (error.message.includes('timeout')) {
        throw new Error('Tiempo de espera agotado al conectar con OpenAI');
      } else if (error.message.includes('network')) {
        throw new Error('Error de conexión. Verifica tu conexión a internet');
      }
      
      throw new Error(`Error al conectar con OpenAI: ${error.message}`);
    }
  }

  async getConfig(configId: string): Promise<OpenAIConfig> {
    try {
      // Return cached config if exists
      if (this.configs.has(configId)) {
        return this.configs.get(configId)!;
      }

      // Get API configuration
      const { data: config, error } = await supabase
        .from('api_configuraciones')
        .select('*')
        .eq('id', configId)
        .single();

      if (error) throw new Error(`Error al obtener la configuración: ${error.message}`);
      if (!config) throw new Error('Configuración no encontrada');
      if (!config.api_key) throw new Error('API key no configurada');
      if (config.estado !== 'activo') throw new Error('La configuración de API no está activa');

      const openAIConfig: OpenAIConfig = {
        apiKey: config.api_key.startsWith('sk-') ? config.api_key : `sk-${config.api_key}`,
        organizationId: config.organization_id || undefined
      };

      // Cache config
      this.configs.set(configId, openAIConfig);
      return openAIConfig;
    } catch (error: any) {
      throw new Error(`Error al obtener la configuración: ${error.message}`);
    }
  }

  async createVectorStore(configId: string, name: string, fileIds?: string[]): Promise<string> {
    try {
      const config = await this.getConfig(configId);

      // Add files one by one to avoid the file_ids issue
      const response = await fetch(`${this.API_BASE_URL}/vector_stores`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.apiKey}`,
          'Content-Type': 'application/json',
          'OpenAI-Beta': 'assistants=v2'
        },
        body: JSON.stringify({
          name,
          metadata: { created_by: 'OpenAIService', assistant_name: name }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Error creating vector store');
      }

      const data = await response.json();

      // If we have files, add them to the vector store
      if (fileIds && fileIds.length > 0) {
        for (const fileId of fileIds) {
          await this.addFileToVectorStore(configId, data.id, fileId);
        }
      }

      return data.id;
    } catch (error: any) {
      throw new Error(`Error creating vector store: ${error.message}`);
    }
  }

  async addFileToVectorStore(configId: string, vectorStoreId: string, fileId: string): Promise<void> {
    try {
      const config = await this.getConfig(configId);

      const response = await fetch(`${this.API_BASE_URL}/vector_stores/${vectorStoreId}/files`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.apiKey}`,
          'Content-Type': 'application/json',
          'OpenAI-Beta': 'assistants=v2'
        },
        body: JSON.stringify({
          file_id: fileId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Error adding file to vector store');
      }
    } catch (error: any) {
      throw new Error(`Error adding file to vector store: ${error.message}`);
    }
  }

  async addFilesToVectorStore(configId: string, vectorStoreId: string, fileIds: string[]): Promise<void> {
    try {
      // Add files one by one to avoid the file_ids issue
      for (const fileId of fileIds) {
        await this.addFileToVectorStore(configId, vectorStoreId, fileId);
      }
    } catch (error: any) {
      throw new Error(`Error adding files to vector store: ${error.message}`);
    }
  }

  async getVectorStore(configId: string, vectorStoreId: string): Promise<VectorStore> {
    try {
      const config = await this.getConfig(configId);

      const response = await fetch(`${this.API_BASE_URL}/vector_stores/${vectorStoreId}`, {
        headers: {
          'Authorization': `Bearer ${config.apiKey}`,
          'Content-Type': 'application/json',
          'OpenAI-Beta': 'assistants=v2'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Error retrieving vector store');
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      throw new Error(`Error retrieving vector store: ${error.message}`);
    }
  }

  async associateVectorStore(configId: string, assistantId: string, vectorStoreId: string): Promise<void> {
    try {
      const config = await this.getConfig(configId);

      const response = await fetch(`${this.API_BASE_URL}/assistants/${assistantId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.apiKey}`,
          'Content-Type': 'application/json',
          'OpenAI-Beta': 'assistants=v2'
        },
        body: JSON.stringify({
          tools: [{ type: "file_search" }],
          tool_resources: {
            file_search: {
              vector_store_ids: [vectorStoreId]
            }
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Error associating vector store');
      }
    } catch (error: any) {
      throw new Error(`Error associating vector store: ${error.message}`);
    }
  }

  async generateResponse(
    configId: string,
    messages: { role: 'user' | 'assistant'; content: string }[]
  ): Promise<{ response: string; openai_conversation_id: string }> {
    try {
      // Get OpenAI client
      const client = await this.getClient(configId);

      // Validate messages
      const validMessages = messages.filter(msg => 
        msg.content && typeof msg.content === 'string' && msg.content.trim() !== ''
      );

      if (validMessages.length === 0) {
        throw new Error('No hay mensajes válidos para procesar');
      }

      // Create chat completion
      const completion = await client.chat.completions.create({
        model: 'gpt-4', // Default to GPT-4
        messages: validMessages.map(msg => ({
          role: msg.role,
          content: msg.content.trim()
        })),
        temperature: 0.7,
        max_tokens: 4096,
        top_p: 1,
        frequency_penalty: 0,
        presence_penalty: 0,
        stream: false,
        n: 1,
        response_format: { type: 'text' }
      });

      const responseContent = completion.choices[0]?.message?.content?.trim();
      if (!responseContent) {
        throw new Error('No se recibió una respuesta válida del modelo');
      }

      return {
        response: responseContent,
        openai_conversation_id: completion.id
      };

    } catch (error: any) {
      // Handle specific OpenAI API errors
      if (error.status === 401) {
        throw new Error('API key inválida o expirada');
      } else if (error.status === 429) {
        throw new Error('Se ha excedido el límite de la API');
      } else if (error.status === 400) {
        if (error.message.includes('content')) {
          throw new Error('Error en el formato de los mensajes');
        } else {
          throw new Error('Error en la solicitud: ' + error.message);
        }
      } else if (error.message.includes('timeout')) {
        throw new Error('La solicitud ha excedido el tiempo límite');
      } else if (error.message.includes('network')) {
        throw new Error('Error de conexión. Verifica tu conexión a internet');
      }
      
      throw new Error('Error al generar la respuesta. Por favor, intenta de nuevo.');
    }
  }

  clearCache(configId?: string) {
    if (configId) {
      this.clients.delete(configId);
      this.configs.delete(configId);
    } else {
      this.clients.clear();
      this.configs.clear();
    }
  }
}

// Export singleton instance
export const openAIService = new OpenAIService();